import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import memory from '../../utils/memory';

// Initial state
const initialState = {
  // Age verification
  isVerified: false,
  verificationTimestamp: null,
  verificationExpiry: 7 * 60 * 1000, // 7 minutes

  // Splash screen
  hasShownSplash: false,
  splashDuration: 3000, // 3 seconds

  // App preferences
  preferences: {
    showAgeVerification: true,
    autoPlayVideos: false,
    enableNotifications: true,
    analyticsEnabled: true,
    performanceMonitoring: true,
  },

  // Session management
  sessionData: null,
  sessionTimeout: 7 * 60 * 1000, // 7 minutes
  inactivityTimer: null,

  // App state
  isInitialized: false,
  currentPage: 'home',
  visitedPages: [],

  // Performance
  appMetrics: {
    totalVisits: 0,
    averageSessionTime: 0,
    lastVisit: null,
    pageViews: {},
  },

  // Error tracking
  errorLog: [],

  // Feature flags
  features: {
    ageVerification: true,
    splashScreen: true,
    analytics: true,
    performanceMonitoring: true,
    errorReporting: true,
  },
};

// Async thunks
export const initializeApp = createAsyncThunk(
  'app/initializeApp',
  async (_, { rejectWithValue }) => {
    try {
      // Load session data from localStorage
      const sessionData = JSON.parse(
        localStorage.getItem('sessionData') || 'null'
      );

      // Update memory with visit info
      memory.updateVisitInfo();

      // Check if session is still valid
      let isVerified = false;
      if (sessionData) {
        const { verified, timestamp } = sessionData;
        const currentTime = Date.now();
        if (currentTime - timestamp < initialState.sessionTimeout) {
          isVerified = verified;
        } else {
          localStorage.removeItem('sessionData');
        }
      }

      return {
        sessionData,
        isVerified,
        isInitialized: true,
      };
    } catch (error) {
      return rejectWithValue({
        message: 'Failed to initialize app',
        error: error.message,
      });
    }
  }
);

export const verifyAge = createAsyncThunk(
  'app/verifyAge',
  async (_, { rejectWithValue }) => {
    try {
      const sessionData = {
        verified: true,
        timestamp: Date.now(),
      };

      localStorage.setItem('sessionData', JSON.stringify(sessionData));

      // Update memory with age verification preference
      memory.updatePreferences({ showAgeVerification: false });

      return sessionData;
    } catch (error) {
      return rejectWithValue({
        message: 'Failed to verify age',
        error: error.message,
      });
    }
  }
);

export const updatePreferences = createAsyncThunk(
  'app/updatePreferences',
  async (newPreferences, { getState, rejectWithValue }) => {
    try {
      const currentPreferences = getState().app.preferences;
      const updatedPreferences = { ...currentPreferences, ...newPreferences };

      // Save to localStorage
      localStorage.setItem(
        'appPreferences',
        JSON.stringify(updatedPreferences)
      );

      // Update memory
      memory.updatePreferences(updatedPreferences);

      return updatedPreferences;
    } catch (error) {
      return rejectWithValue({
        message: 'Failed to update preferences',
        error: error.message,
      });
    }
  }
);

export const trackPageView = createAsyncThunk(
  'app/trackPageView',
  async (pageName, { getState }) => {
    try {
      const currentMetrics = getState().app.appMetrics;
      const updatedPageViews = {
        ...currentMetrics.pageViews,
        [pageName]: (currentMetrics.pageViews[pageName] || 0) + 1,
      };

      return {
        pageName,
        pageViews: updatedPageViews,
        totalVisits: currentMetrics.totalVisits + 1,
        lastVisit: new Date().toISOString(),
      };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.warn('Failed to track page view:', error);
      return { pageName, error: error.message };
    }
  }
);

// App slice
const appSlice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    // Age verification
    setVerified: (state, action) => {
      state.isVerified = action.payload;
      if (action.payload) {
        state.verificationTimestamp = Date.now();
      } else {
        state.verificationTimestamp = null;
      }
    },

    // Splash screen
    setHasShownSplash: (state, action) => {
      state.hasShownSplash = action.payload;
    },

    // Session management
    updateSessionData: (state, action) => {
      state.sessionData = action.payload;
    },

    // App state
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
      if (!state.visitedPages.includes(action.payload)) {
        state.visitedPages.push(action.payload);
      }
    },

    // Performance
    updateAppMetrics: (state, action) => {
      state.appMetrics = { ...state.appMetrics, ...action.payload };
    },

    // Error tracking
    addError: (state, action) => {
      const error = {
        id: Date.now(),
        timestamp: new Date().toISOString(),
        ...action.payload,
      };
      state.errorLog.push(error);

      // Keep only last 50 errors
      if (state.errorLog.length > 50) {
        state.errorLog = state.errorLog.slice(-50);
      }
    },

    clearErrors: state => {
      state.errorLog = [];
    },

    // Feature flags
    toggleFeature: (state, action) => {
      const { feature, enabled } = action.payload;
      if (state.features.hasOwnProperty(feature)) {
        state.features[feature] = enabled;
      }
    },

    // Reset app state
    resetAppState: state => {
      return {
        ...initialState,
        preferences: state.preferences, // Preserve preferences
        features: state.features, // Preserve feature flags
      };
    },
  },
  extraReducers: builder => {
    builder
      // Initialize app
      .addCase(initializeApp.pending, state => {
        state.isInitialized = false;
      })
      .addCase(initializeApp.fulfilled, (state, action) => {
        state.isInitialized = true;
        state.sessionData = action.payload.sessionData;
        state.isVerified = action.payload.isVerified;
        if (action.payload.isVerified) {
          state.verificationTimestamp = Date.now();
        }
      })
      .addCase(initializeApp.rejected, (state, action) => {
        state.isInitialized = false;
        state.errorLog.push({
          id: Date.now(),
          timestamp: new Date().toISOString(),
          type: 'initialization',
          message: action.payload.message,
          error: action.payload.error,
        });
      })

      // Verify age
      .addCase(verifyAge.fulfilled, (state, action) => {
        state.isVerified = true;
        state.verificationTimestamp = Date.now();
        state.sessionData = action.payload;
      })

      // Update preferences
      .addCase(updatePreferences.fulfilled, (state, action) => {
        state.preferences = action.payload;
      })

      // Track page view
      .addCase(trackPageView.fulfilled, (state, action) => {
        if (action.payload.pageViews) {
          state.appMetrics.pageViews = action.payload.pageViews;
          state.appMetrics.totalVisits = action.payload.totalVisits;
          state.appMetrics.lastVisit = action.payload.lastVisit;
        }
      });
  },
});

// Export actions
export const {
  setVerified,
  setHasShownSplash,
  updateSessionData,
  setCurrentPage,
  updateAppMetrics,
  addError,
  clearErrors,
  toggleFeature,
  resetAppState,
} = appSlice.actions;

// Selectors
export const selectApp = state => state.app;
export const selectIsVerified = state => state.app.isVerified;
export const selectVerificationTimestamp = state =>
  state.app.verificationTimestamp;
export const selectHasShownSplash = state => state.app.hasShownSplash;
export const selectPreferences = state => state.app.preferences;
export const selectSessionData = state => state.app.sessionData;
export const selectIsInitialized = state => state.app.isInitialized;
export const selectCurrentPage = state => state.app.currentPage;
export const selectVisitedPages = state => state.app.visitedPages;
export const selectAppMetrics = state => state.app.appMetrics;
export const selectErrorLog = state => state.app.errorLog;
export const selectFeatures = state => state.app.features;

// Export reducer
export default appSlice.reducer;
